import { ConfigProvider, notification, Spin } from "antd";
import useDrafts from "hooks/useDrafts";
import useUserLoans from "hooks/useUserLoans";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { DASHBOARD_APPLY_ROUTE, DASHBOARD_APPLY_UNDERGRAD_ROUTE } from "routes";
import { BASE_URL } from "services";
import { getLoanProfile } from "services/LoanService";
import http from "services/httpService";
import useSWR from "swr";

export default function Application() {
  const { loading, userLoans } = useUserLoans({
    status: "disbursed",
  });

  const [checkingProfile, setCheckingProfile] = useState(false);

  const url = `${BASE_URL}/users/who_i_am`;

  const fetcher = async () => {
    const res = await http.get(url);
    return res?.data?.data;
  };

  const { data: user } = useSWR(url, fetcher);

  const { incompleteLoans } = useDrafts();

  const canApply = useMemo(() => {
    if (!userLoans || userLoans.length === 0) return true;

    const sortedLoans = [...userLoans].sort((a, b) => {
      const getDate = (loan) =>
        new Date(loan.disburse_date || loan.disbursedAt || loan.created_at);

      return getDate(b).getTime() - getDate(a).getTime();
    });

    const recent = sortedLoans[0];

    const rawDate =
      recent.disburse_date || recent.disbursedAt || recent.created_at;

    if (!rawDate) return true;

    const disbursedDate = DateTime.fromJSDate(new Date(rawDate));

    const monthsPassed = DateTime.now().diff(disbursedDate, "months").months;

    return monthsPassed >= 5;
  }, [userLoans]);

  const router = useNavigate();

  const showBlockMessage = (
    description = "Eligibility for a new loan requires at least 5 months to have elapsed since your most recent disbursement.",
  ) =>
    notification.warning({
      placement: "top",
      duration: 20,
      message: "Your application cannot proceed at this time.",
      description,
    });

  const checkEligibility = () => {
    if (!canApply) {
      showBlockMessage();
      return false;
    }

    if (!user?.nin || !user?.nin_verified) {
      showBlockMessage("Please verify your NIN before applying for a loan.");
      return false;
    }

    return true;
  };

  const goToRoute = (route) => () => {
    if (!checkEligibility()) return;

    router(route);
  };

  const handleApply = async () => {
    if (!checkEligibility()) return;

    if (incompleteLoans.length > 0) {
      toast.error(
        "You have an incomplete application. Use the list below to complete it",
      );
      return;
    }

    setCheckingProfile(true);

    try {
      const res = await getLoanProfile();

      const loanProfile = res?.data?.data;

      if (loanProfile) {
        router("/dashboard/reapply");
      } else {
        router(DASHBOARD_APPLY_UNDERGRAD_ROUTE);
      }
    } catch (error) {
      /*
       * A missing loan profile is expected for users who have
       * never completed an application.
       *
       * In that case, simply use the existing application flow.
       */
      router(DASHBOARD_APPLY_UNDERGRAD_ROUTE);
    } finally {
      setCheckingProfile(false);
    }
  };

  return (
    <div className="bg-white border border-solid border-gray-300 p-2 rounded-lg">
      <header className="flex justify-between py-2 border-b border-secondary-100 border-solid">
        <p className="flex gap-2 text-sm sm:text-lg text-gray-[#242424] items-center">
          <span>
            <i className="bi bi-folder-plus"></i>
          </span>

          <span className="font-medium">Apply For Loan</span>
        </p>

        <button
          onClick={goToRoute(DASHBOARD_APPLY_ROUTE)}
          className="bg-primary-100 border border-solid border-secondary-500 text-secondary-600 px-2 sm:px-4 py-2 text-xs sm:text-sm rounded-full"
        >
          See Applications
        </button>
      </header>

      <div className="flex justify-center py-4 mt-4">
        <button
          disabled={loading || checkingProfile}
          onClick={handleApply}
          className="bg-secondary flex items-center gap-x-3 disabled:opacity-60 text-white px-6 py-2 text-sm sm:text-base rounded-full"
        >
          {checkingProfile
            ? "Checking your profile..."
            : "Apply for Undergraduate Loan"}

          {(loading || checkingProfile) && (
            <ConfigProvider
              theme={{
                components: {
                  Spin: {
                    colorPrimary: "#ffffff",
                  },
                },
              }}
            >
              <Spin />
            </ConfigProvider>
          )}
        </button>
      </div>
    </div>
  );
}
